import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, Sparkles } from "lucide-react";
import { HERO_KEYWORDS, CONTACT } from "../data/content";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.3 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      variants={line}
      custom={i}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const [kw, setKw] = useState(0);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 120]);
  const textY = useTransform(scrollY, [0, 800], [0, -60]);

  useEffect(() => {
    const t = setInterval(() => setKw((k) => (k + 1) % HERO_KEYWORDS.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden"
    >
      {/* Background orbs */}
      <div className="orb w-[500px] h-[500px] bg-jo-accent/40 -top-40 -left-32" />
      <div className="orb w-[420px] h-[420px] bg-jo-highlight/30 top-1/3 -right-24" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        {/* Left copy */}
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-white/60 px-4 py-1.5 mb-7"
          >
            <Sparkles size={15} className="text-jo-highlight" />
            <span className="text-xs font-semibold tracking-wider text-jo-primary">
              8+ YEARS OF BUSINESS EXCELLENCE
            </span>
          </motion.div>

          <h1 className="font-bold tracking-tighter text-jo-primary leading-[0.95] text-5xl md:text-6xl lg:text-[5.2rem]">
            <MaskLine i={0}>Delivering</MaskLine>
            <MaskLine i={1}>Business Excellence</MaskLine>
            <span className="block overflow-hidden">
              <motion.span
                className="block"
                variants={line}
                custom={2}
                initial="hidden"
                animate="visible"
              >
                Through{" "}
                <span className="relative inline-block align-top min-w-[3ch] text-jo-highlight">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={kw}
                      data-testid="hero-rotating-keyword"
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -30, opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="inline-block"
                    >
                      {HERO_KEYWORDS[kw]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-base md:text-lg text-slate-600 font-light leading-relaxed"
          >
            Johar Softech provides IT Services, Healthcare Outsourcing, BPO Solutions and
            Business Process Management to organizations across India and global markets —
            helping businesses improve efficiency, reduce costs and accelerate growth.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-get-started"
              onClick={() => scrollTo("#contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-jo-primary text-white font-semibold px-7 py-4 text-sm hover:bg-jo-highlight transition-colors duration-300 shadow-xl shadow-jo-primary/20"
            >
              Get Started
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-contact-us"
              onClick={() => scrollTo("#contact")}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-jo-primary/15 text-jo-primary font-semibold px-7 py-4 text-sm hover:border-jo-accent hover:text-jo-accent transition-colors duration-300"
            >
              <Phone size={17} /> Contact Us
            </button>
          </motion.div>
        </motion.div>

        {/* Right image, clipped frame with parallax */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-jo-primary/20 border-4 border-white"
          >
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
              alt="Corporate professionals at Johar Softech"
              className="w-full h-[440px] lg:h-[540px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-jo-primary/40 via-transparent to-transparent" />
          </motion.div>

          {/* Floating stat card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="absolute -bottom-6 -left-6 glass rounded-2xl px-6 py-4"
          >
            <div className="text-3xl font-bold text-jo-primary">99%</div>
            <div className="text-xs text-slate-500 font-medium">Client Satisfaction</div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.7 }}
            className="absolute -top-5 -right-4 glass rounded-2xl px-6 py-4"
          >
            <div className="text-3xl font-bold text-jo-highlight">150+</div>
            <div className="text-xs text-slate-500 font-medium">Projects Delivered</div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
